/* eslint-disable import/first */
/* eslint-disable strict */
"use client";

import React from "react";
import {
  Attributes,
  ProviderProps,
  Dispatch,
  SetStateAction,
  createElement,
  useState,
  useContext,
  createContext,
  Fragment,
} from "react";

/**
 * This hook will create shared state between components which can be accessed
 * and modified in a similar manner to useState
 *
 * @template T
 * @param {T} defaultInitialValue
 */
export const createStateContext = <T>(defaultInitialValue: T) => {
  const context = createContext<[T, React.Dispatch<React.SetStateAction<T>>] | undefined>(undefined);
  const providerFactory = (
    props: (Attributes & ProviderProps<[T, Dispatch<SetStateAction<T>>] | undefined>) | null | undefined,
    children: React.ReactNode | null | undefined
  ) => createElement(context.Provider, props, children);

  const StateProvider: React.FC<React.PropsWithChildren<{ initialValue?: T }>> = ({ children, initialValue }) => {
    const state = useState<T>(initialValue !== undefined ? initialValue : defaultInitialValue);
    return providerFactory({ value: state }, children);
  };

  const useStateContext = () => {
    const state = useContext(context);
    if (state === undefined) {
      throw new Error(`useStateContext must be used inside a StateProvider.`);
    }
    return state;
  };

  return [useStateContext, StateProvider, context] as const;
};

/**
 * Creates a context with multiple independent state values that can be accessed
 * and modified independently without causing the entire component tree to re-render.
 *
 * @template T - An object type containing the shape of your state
 * @param {T} defaultValues - The default values for all state properties
 * @param {Object} options - Configuration options for the context
 * @param {string} options.storageKey - The key to use for local storage
 * @param {Array<keyof T>} options.persistedKeys - Array of keys to persist in local storage
 * @returns An object with useContextState, StateProvider, context, and createStateHook
 */
export const createMultiStateContext = <T extends Record<string, any>>(
  defaultValues: T,
  options?: {
    storageKey?: string;
    persistedKeys?: Array<keyof T>;
  }
) => {
  // Create individual contexts for each state key
  type ContextMap = {
    [K in keyof T]: ReturnType<typeof createStateContext<T[K]>>;
  };

  // Load persisted values from local storage
  const loadPersistedValues = (): Partial<T> => {
    if (!options?.storageKey || !options?.persistedKeys?.length) return {};

    try {
      const stored = localStorage.getItem(options.storageKey);
      if (!stored) return {};

      const parsed = JSON.parse(stored);
      return options.persistedKeys.reduce((acc, key) => {
        if (parsed[key] !== undefined) {
          acc[key] = parsed[key];
        }
        return acc;
      }, {} as Partial<T>);
    } catch {
      return {};
    }
  };

  // Save values to local storage
  const saveToStorage = (values: Partial<T>) => {
    if (!options?.storageKey || !options?.persistedKeys?.length) return;

    try {
      const current = loadPersistedValues();
      const updated = { ...current, ...values };
      localStorage.setItem(options.storageKey, JSON.stringify(updated));
    } catch {
      return;
    }
  };

  // Create individual state contexts for each key
  const contextMap = Object.keys(defaultValues).reduce((acc, key) => {
    const typedKey = key as keyof T;
    acc[typedKey] = createStateContext(defaultValues[typedKey]);
    return acc;
  }, {} as Partial<ContextMap>) as ContextMap;

  // Combined provider that wraps all individual providers
  const StateProvider: React.FC<React.PropsWithChildren<{ initialValues?: Partial<T> }>> = ({ children, initialValues }) => {
    // Load persisted values
    const persistedValues = loadPersistedValues();

    // Start with the children
    let wrappedChildren = children;

    // Wrap the children in each provider, from innermost to outermost
    Object.keys(defaultValues)
      .reverse()
      .forEach((key) => {
        const typedKey = key as keyof T;
        const [, Provider] = contextMap[typedKey];

        // Use persisted value if available, otherwise use provided initial value or default
        const initialValue =
          persistedValues[typedKey] ??
          (initialValues && initialValues[typedKey] !== undefined ? initialValues[typedKey] : undefined);

        wrappedChildren = createElement(Provider, { initialValue }, wrappedChildren);
      });

    return createElement(Fragment, null, wrappedChildren);
  };

  /**
   * Hook to access a specific state value from the context
   * @param key - The key of the state value to access
   * @returns [state, setState] for the specified key
   */
  const useContextState = <K extends keyof T>(key: K): [T[K], Dispatch<SetStateAction<T[K]>>] => {
    const [useStateHook] = contextMap[key];
    const [state, setState] = useStateHook();

    // Create a wrapped setState that also saves to storage
    const wrappedSetState = (value: SetStateAction<T[K]>) => {
      setState(value);
      if (options?.persistedKeys?.includes(key)) {
        const newValue = typeof value === "function" ? (value as Function)(state) : value;
        saveToStorage({ [key]: newValue } as Partial<T>);
      }
    };

    return [state, wrappedSetState];
  };

  /**
   * Creates a custom hook for a specific state value
   * @param key - The key of the state value to access
   * @returns A hook that provides [state, setState] for the specified key
   */
  const createStateHook = <K extends keyof T>(key: K) => {
    const [useStateHook] = contextMap[key];
    return () => {
      const [state, setState] = useStateHook();

      // Create a wrapped setState that also saves to storage
      const wrappedSetState = (value: SetStateAction<T[K]>) => {
        setState(value);
        if (options?.persistedKeys?.includes(key)) {
          const newValue = typeof value === "function" ? (value as Function)(state) : value;
          saveToStorage({ [key]: newValue } as Partial<T>);
        }
      };

      return [state, wrappedSetState] as [T[K], Dispatch<SetStateAction<T[K]>>];
    };
  };

  return {
    useContextState,
    StateProvider,
    contextMap,
    createStateHook,
  };
};

/* ---------------------------------- Usage --------------------------------- */

// Example usage with createStateContext:
// const [useSharedText, SharedTextProvider] = createStateContext('');

// Example usage with createMultiStateContext:
// const { useContextState, StateProvider: MultiStateProvider, createStateHook } = createMultiStateContext({
//   text: '',
//   count: 0,
//   isOpen: false
// });
//
// // Create individual hooks for specific state values
// const useText = createStateHook('text');
// const useCount = createStateHook('count');
// const useIsOpen = createStateHook('isOpen');
//
// // Use them in components
// const TextComponent = () => {
//   const [text, setText] = useText();
//   return <input value={text} onChange={e => setText(e.target.value)} />;
// };
//
// const CountComponent = () => {
//   const [count, setCount] = useCount();
//   return <button onClick={() => setCount(count + 1)}>{count}</button>;
// };
//
// // Or use the generic hook with a key
// const AnotherComponent = () => {
//   const [isOpen, setIsOpen] = useContextState('isOpen');
//   return <button onClick={() => setIsOpen(!isOpen)}>{isOpen ? 'Close' : 'Open'}</button>;
// };
//
// const App = () => (
//   <MultiStateProvider>
//     <TextComponent />
//     <CountComponent />
//     <AnotherComponent />
//   </MultiStateProvider>
// );
